import { Avatar, Box, Typography } from "@dealer-center/core-components";
import { useGetUserInfo } from "@dealer-center/ddc-pages-configuration-fetch";
import type { MyInfoSubject } from "../../useMyInfoSubject";
import {
  ON_SURFACE_INVERSE,
  SPACE_BLOCK,
  SURFACE_INTERACTIVE_REST
} from "../../layoutTokens";

export interface IdentityHeaderProps {
  subject: MyInfoSubject;
}

/**
 * FR-004's identity header: avatar, name, username, account. The card's first row.
 *
 * **Both the name and the account line come from `useGetUserInfo().data.summary`, not from
 * `useMyInfoSubject`** — see the note beside each below. For the account line (Notes → gap 1): the
 * outlet context carries `accountId` but no account name, so the subject hook deliberately exposes
 * none — the only value it could return is the id, which would then be rendered as if it were the
 * name. Calling `useGetUserInfo` here costs no second request: React Query dedupes it against the
 * User Information tab's identical query key.
 *
 * Known limitation, accepted: `company` is a per-user, user-editable profile field, so it can
 * diverge from the account's real name (of 12 sampled `dealerdemo` users, `jlwood2420` read
 * "Marketing Team"). Still better than rendering the raw account id; the durable fix is a BFF
 * endpoint over CC9 `/serviceAccounts/{accountId}`, out of scope here.
 *
 * FR-003's `h1` is **not** here — it's page furniture outside the card, in `PageHeading`.
 */
export const IdentityHeader = ({ subject }: IdentityHeaderProps) => {
  const { data } = useGetUserInfo({
    userId: subject.userId,
    accountId: subject.accountId
  });

  const summary = data?.summary;

  const company = summary?.company?.trim();
  const accountLine = company ? company : subject.accountId;

  /*
   * The name comes from the **profile record**, not from `subject.displayName`, for the same reason
   * the account line comes from `summary.company`: it is the value this page is about.
   *
   * `subject.displayName` is built from the outlet context's `first`/`last`, which the ControlCenter
   * lambda copies straight off the **session JWT** — it describes whoever is signed in. In an "as"
   * context the lambda swaps `userId`/`accountId` to the as-user but passes `first`/`last` through
   * untouched, so the header rendered the operator's name above the as-user's record — and directly
   * above the First Name / Last Name fields holding the *other* name. Reading `summary` makes the
   * header agree with the fields it sits on top of, by construction.
   *
   * Falls back to `subject.displayName` while the query is pending and when the record carries
   * neither name (CC9 returns `""`, not null, for unset strings) — so the header is never blank.
   */
  const profileName = [summary?.firstName, summary?.lastName]
    .filter(part => Boolean(part?.trim()))
    .join(" ")
    .trim();
  const displayName = profileName || subject.displayName;

  /*
   * The avatar is decorative: it carries the name's first letter and no information the adjacent
   * text does not already state, so it is hidden from assistive technology rather than announced as
   * a redundant single character.
   */
  const initial = displayName.trim().charAt(0).toUpperCase();

  return (
    <Box
      sx={{
        alignItems: "center",
        columnGap: SPACE_BLOCK,
        display: "flex",
        rowGap: "8px"
      }}
    >
      {/*
       * `sizes="lg"` is Interstate's largest step (48px). The prototype draws 56px, which the scale
       * has no step for — 48px is taken over an `sx` width/height so the avatar stays on the design
       * system's scale; the 8px is imperceptible beside the name.
       *
       * The **fill**, though, has to be overridden, and the nested selector is load-bearing.
       * Interstate styles its own avatar with a self-referencing `.css-<hash>.MuiAvatar-root` rule —
       * two classes, so specificity (0,2,0) — which silently beats a plain `sx` object at (0,1,0):
       * the avatar rendered as the default grey `#e3e3e3` with black text and the `sx` colours were
       * dropped on the floor with no warning. `&.MuiAvatar-root` matches that specificity so the
       * approved blue actually applies.
       *
       * This is the one place in the frame that reaches past a component's public API, so it is the
       * one place an Interstate upgrade could regress — if the avatar ever renders grey again, this
       * selector is why.
       */}
      <Avatar
        aria-hidden={"true"}
        data-testid={"myinfo-avatar"}
        sizes={"lg"}
        sx={{
          "&.MuiAvatar-root": {
            backgroundColor: SURFACE_INTERACTIVE_REST,
            color: ON_SURFACE_INVERSE
          }
        }}
      >
        {initial}
      </Avatar>

      <Box>
        <Box
          sx={{
            alignItems: "baseline",
            columnGap: "8px",
            display: "flex",
            flexWrap: "wrap"
          }}
        >
          <Typography
            data-testid={"myinfo-display-name"}
            tag={"p"}
            variant={"h3"}
          >
            {displayName}
          </Typography>
          {/*
           * `tag="span"` with an `h3`-scaled sibling: the username sits on the same line at the same
           * size but unemphasised, which is the prototype's `.uid` treatment. It is not a heading and
           * must not become one — the page's outline is `h1` → the tabs' section headings, and an
           * `h3` element here would wedge a level in above them.
           */}
          <Typography
            color={"sem.color.on-surface.muted"}
            data-testid={"myinfo-username"}
            tag={"span"}
            variant={"body-lg"}
          >
            {`(${subject.username})`}
          </Typography>
        </Box>

        <Typography
          data-testid={"myinfo-account-line"}
          tag={"p"}
          variant={"strong-sm"}
        >
          {accountLine}
        </Typography>
      </Box>
    </Box>
  );
};
